import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ContactInfoService } from '../../services/contact-info.service';
import { ContactInfo } from '../../contact-info';
import { ValidatorService } from '../../../../angular-shared/services/validators-reactive.service';
import { Utils } from '../../../Utils';
import { CityListService } from '../../../common/services/cityList.service';

@Component({
  selector: 'contact-update',
  templateUrl: './contact-update.component.html',
})
export class ContactUpdateComponent {
  @Input() contactInfo: ContactInfo;
  @Input() childMode: boolean;
  @Output() sameAddressChange = new EventEmitter<boolean>();
  contactFormGroup: FormGroup;
  useSameAddress: boolean = false;
  stateList = Utils.getStateList();
  cityList: string[];
  lastZip: string = '';
  isZipLoading: boolean = false;

  constructor(protected contactUpdateService: ContactInfoService, protected formBuilder: FormBuilder) {
    this.contactFormGroup = this.formBuilder.group({
      street: ['', Validators.required],
      line1: [''],
      line2: [''],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zip: ['', ValidatorService.zipValidator],
      phone: ['', [ValidatorService.phoneValidator, Validators.required]],
    });
  }

  //todo: remove duplicate code!
  formatPhone(phone: string): string {
    if (phone && phone !== '') {
      const numbers = this.removeSymbols(phone);
      if (numbers.length === 10) {
        return '(' + numbers.substring(0, 3) + ') ' + numbers.substring(3, 6) + '-' + numbers.substring(6);
      } else if (numbers.length > 10) {
        return (
          '(' +
          numbers.substring(0, 3) +
          ') ' +
          numbers.substring(3, 6) +
          '-' +
          numbers.substring(6, 10) +
          ' x' +
          numbers.substring(10)
        );
      } else {
        return phone;
      }
    } else {
      return phone;
    }
  }

  removeSymbols(phone: string): string {
    return phone.replace(/\D/g, '');
  }

  async zipCodeLookup(zip: string): Promise<void> {
    this.isZipLoading = true;

    if (this.lastZip === this.contactFormGroup.get('zip').value) {
      return;
    }
    this.lastZip = this.contactFormGroup.get('zip').value;

    const location = await this.contactUpdateService.getLocation(zip);

    if (!location) {
      this.contactFormGroup.get('zip').setErrors({ unsupportedZip: true });
      this.isZipLoading = false;
      return;
    }

    const cityListObj = new CityListService(CityListService.CHANNEL_HAVEN, location);
    this.cityList = cityListObj.getCityList();

    this.contactFormGroup.patchValue({
      city: cityListObj.getCity(),
      state: cityListObj.getStateCode(),
    });
    this.isZipLoading = false;
  }

  getContactInfo(): ContactInfo {
    return new ContactInfo(
      this.contactInfo.firstName,
      this.contactInfo.lastName,
      this.contactFormGroup.get('street').value,
      this.contactFormGroup.get('line1').value,
      this.contactFormGroup.get('line2').value,
      this.contactFormGroup.get('city').value,
      this.contactFormGroup.get('state').value,
      this.contactFormGroup.get('zip').value,
      this.contactFormGroup.get('phone').value,
      this.contactInfo.id,
      this.contactInfo.type
    );
  }

  updateContactInfo(contactInfo: ContactInfo): void {
    this.contactFormGroup.patchValue({
      street: contactInfo.street,
      line1: contactInfo.line1,
      line2: contactInfo.line2,
      city: contactInfo.city,
      state: contactInfo.state,
      zip: contactInfo.zip,
      phone: this.formatPhone(contactInfo.phoneNumber),
    });
  }

  updateContactInfoWithoutPhone(contactInfo: ContactInfo) {
    this.contactFormGroup.patchValue(contactInfo);
  }

  changeSameAddress(checked: boolean, emit = true) {
    this.useSameAddress = checked;
    if (checked) {
      this.contactFormGroup.get('street').disable();
      this.contactFormGroup.get('line1').disable();
      this.contactFormGroup.get('line2').disable();
      this.contactFormGroup.get('city').disable();
      this.contactFormGroup.get('state').disable();
      this.contactFormGroup.get('zip').disable();
    } else {
      this.contactFormGroup.enable();
    }
    if (emit) {
      this.sameAddressChange.emit(checked);
    }
  }

  checkChanged(): boolean {
    return (
      this.contactInfo &&
      (this.contactInfo.street !== this.contactFormGroup.get('street').value ||
        this.contactInfo.line1 !== this.contactFormGroup.get('line1').value ||
        this.contactInfo.line2 !== this.contactFormGroup.get('line2').value ||
        this.contactInfo.city !== this.contactFormGroup.get('city').value ||
        this.contactInfo.state !== this.contactFormGroup.get('state').value ||
        this.contactInfo.zip !== this.contactFormGroup.get('zip').value ||
        !this.contactInfo.phoneNumber ||
        this.removeSymbols(this.contactInfo.phoneNumber) !==
          this.removeSymbols(this.contactFormGroup.get('phone').value))
    );
  }

  disableSubmit(): boolean {
    //if true, disable submit button
    return this.checkChanged() === false || this.contactFormGroup.invalid || this.isZipLoading;
  }
}
